import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Main as Container } from 'Components/Main';
import { PageLoaderShow } from 'Components/PageLoader';
import { useAuthTenant } from 'store/auth';
import { getTransparencyConfig } from 'services/transparency-config-service';
import { PageHeader, Button } from 'Components/ui';
import styled from 'styled-components';
import { GeneralSettings } from './GeneralSettings';
import { FrameworkVisibility } from './FrameworkVisibility';
import { PolicyVisibility } from './PolicyVisibility';
import { AccessControl } from './AccessControl';
import { TokenManagement } from './TokenManagement';
import { AuditLog } from './AuditLog';

const PillTabBar = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 24px;
  background: white;
  border-radius: 12px;
  padding: 5px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  width: fit-content;
`

const PillTab = styled.button`
  height: 36px;
  padding: 0 16px;
  border: none;
  border-radius: 9px;
  background: ${({ $active }) => $active ? 'var(--color-brand)' : 'transparent'};
  color: ${({ $active }) => $active ? 'white' : 'var(--vo-slate)'};
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: ${({ $active }) => $active ? '600' : '500'};
  cursor: pointer;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background .15s, color .15s;

  &:hover:not([data-active="true"]) {
    background: var(--vo-mist);
    color: var(--vo-ink-2);
  }
`

const Alert = styled.div`
  padding: 12px 16px;
  border-radius: var(--radius-md);
  background-color: var(--vo-danger-10);
  color: var(--vo-danger);
  font-size: 14px;
`

const EyeSVG = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
)


export const TransparencyConfig = () => {
  const navigate = useNavigate();
  const selectedTenant = useSelector(useAuthTenant);
  const { t } = useTranslation(['transparency', 'common']);
  const [activeTab, setActiveTab] = useState('general');

  const { isLoading, error, data: config, refetch } = useQuery(
    ['transparencyConfig', selectedTenant?._id],
    () => getTransparencyConfig(selectedTenant?.slug),
    { enabled: !!selectedTenant?.slug }
  );

  if (isLoading) return <PageLoaderShow />;

  if (error) {
    return (
      <Container>
        <PageHeader
          title={t('transparency:config.errorTitle')}
          segments={[
            { label: t('common:breadcrumbs.manage') },
            { label: t('transparency:config.title') },
          ]}
        />
        <Alert>{t('transparency:config.errorMsg')}</Alert>
      </Container>
    );
  }

  const tabs = [
    { key: 'general', label: t('transparency:config.tabs.general') },
    { key: 'frameworks', label: t('transparency:config.tabs.frameworks') },
    { key: 'policies', label: t('transparency:config.tabs.policies') },
    { key: 'accessControl', label: t('transparency:config.tabs.accessControl') },
    { key: 'tokens', label: t('transparency:config.tabs.tokens') },
    { key: 'audit', label: t('transparency:config.tabs.audit') },
  ];

  return (
    <Container>
      <PageHeader
        title={t('transparency:config.heading')}
        segments={[
          { label: t('common:breadcrumbs.manage') },
          { label: t('transparency:config.title') },
        ]}
        actions={
          <Button variant="outline" onClick={() => navigate('/transparency-center/preview')} startIcon={<EyeSVG />} sx={{ height: '40px', padding: '0 18px' }}>
            {t('transparency:config.previewAction')}
          </Button>
        }
      />

      <PillTabBar>
        {tabs.map(tab => (
          <PillTab
            key={tab.key}
            $active={activeTab === tab.key}
            data-active={activeTab === tab.key}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </PillTab>
        ))}
      </PillTabBar>

      {activeTab === 'general' && (
        <GeneralSettings config={config.data} onUpdate={refetch} companyKey={selectedTenant?.slug} />
      )}
      {activeTab === 'frameworks' && (
        <FrameworkVisibility config={config.data} onUpdate={refetch} companyKey={selectedTenant?.slug} companyId={selectedTenant?.slug} />
      )}
      {activeTab === 'policies' && (
        <PolicyVisibility config={config.data} onUpdate={refetch} companyKey={selectedTenant?.slug} />
      )}
      {activeTab === 'accessControl' && (
        <AccessControl config={config.data} onUpdate={refetch} companyKey={selectedTenant?.slug} />
      )}
      {activeTab === 'tokens' && (
        <TokenManagement config={config.data} companyKey={selectedTenant?.slug} />
      )}
      {activeTab === 'audit' && (
        <AuditLog companyKey={selectedTenant?.slug} />
      )}
    </Container>
  );
};
